import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/layout/Shell";
import { api } from "@/lib/api";
import { PageHeader, PrimaryButton, GhostButton, EmptyState, Field, inputCls } from "@/components/common";
import { StatusPill, ProgressBar, STATUS_LIST } from "@/components/projectShared";
import { Plus, FolderKanban, LayoutGrid, Rows, ListChecks, CalendarDays, BookOpen, Trash2, Repeat, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const initialForm = { client_id: "", name: "", description: "", status: "planeado", team_member_id: "", start_date: "", due_date: "", template_id: "" };

export default function Proyectos() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [view, setView] = useState("kanban");
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [newTplForm, setNewTplForm] = useState({ name: "", description: "", tasksText: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, t, tpl] = await Promise.all([
        api.get("/projects"),
        api.get("/clients"),
        api.get("/team"),
        api.get("/project-templates"),
      ]);
      setProjects(p.data); setClients(c.data); setTeam(t.data); setTemplates(tpl.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.client_id, c])), [clients]);
  const teamMap = useMemo(() => Object.fromEntries(team.map(t => [t.member_id, t])), [team]);

  const openNew = () => {
    setForm({ ...initialForm, client_id: clients[0]?.client_id || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const payload = { ...form };
      if (!payload.team_member_id) delete payload.team_member_id;
      if (!payload.start_date) delete payload.start_date;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.template_id) delete payload.template_id;
      const { data } = await api.post("/projects", payload);
      const tpl = templates.find(t => t.template_id === form.template_id);
      if (tpl) toast.success(`Proyecto creado con ${tpl.task_titles.length} tareas de la plantilla`);
      else toast.success("Proyecto creado");
      setOpen(false);
      navigate(`/proyectos/${data.project_id}`);
    } catch { toast.error("No se pudo crear"); }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!newTplForm.name.trim()) { toast.error("Nombre requerido"); return; }
    const titles = newTplForm.tasksText.split("\n").map(s => s.trim()).filter(Boolean);
    try {
      await api.post("/project-templates", {
        name: newTplForm.name, description: newTplForm.description, task_titles: titles,
      });
      toast.success("Plantilla creada");
      setNewTplForm({ name: "", description: "", tasksText: "" });
      const { data } = await api.get("/project-templates");
      setTemplates(data);
    } catch { toast.error("No se pudo guardar"); }
  };

  const removeTemplate = async (t) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.name}"?`)) return;
    await api.delete(`/project-templates/${t.template_id}`);
    toast.success("Plantilla eliminada");
    const { data } = await api.get("/project-templates");
    setTemplates(data);
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_LIST.map(s => [s.key, []]));
    for (const p of projects) (g[p.status] || g.planeado).push(p);
    return g;
  }, [projects]);

  return (
    <Shell>
      <PageHeader
        title="Proyectos"
        subtitle={`${projects.length} proyectos en gestión`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
              <button
                data-testid="view-kanban-btn"
                onClick={() => setView("kanban")}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 transition-colors ${view === "kanban" ? "bg-[#F97316]/10 text-[#F97316]" : "text-[#9CA3AF] hover:text-[#F5F5F7]"}`}
              >
                <LayoutGrid className="w-3.5 h-3.5"/> Kanban
              </button>
              <button
                data-testid="view-table-btn"
                onClick={() => setView("table")}
                className={`px-3 py-2 text-xs flex items-center gap-1.5 border-l border-[#262A33] transition-colors ${view === "table" ? "bg-[#F97316]/10 text-[#F97316]" : "text-[#9CA3AF] hover:text-[#F5F5F7]"}`}
              >
                <Rows className="w-3.5 h-3.5"/> Tabla
              </button>
            </div>
            <GhostButton data-testid="templates-btn" onClick={()=>setTemplatesOpen(true)}>
              <BookOpen className="w-4 h-4"/> Plantillas
            </GhostButton>
            <PrimaryButton data-testid="add-project-btn" onClick={openNew}>
              <Plus className="w-4 h-4"/> Nuevo proyecto
            </PrimaryButton>
          </div>
        }
      />

      {loading ? (
        <div className="text-[#9CA3AF]">Cargando…</div>
      ) : projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="Aún no tienes proyectos"
          description={clients.length === 0 ? "Primero añade un cliente para poder crear proyectos." : "Crea tu primer proyecto para llevar el seguimiento con tareas, avance y notas."}
          action={clients.length > 0 && <PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Crear proyecto</PrimaryButton>}
        />
      ) : view === "kanban" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STATUS_LIST.map(col => (
            <div key={col.key} data-testid={`kanban-col-${col.key}`} className="bg-[#15171E] border border-[#262A33] rounded-2xl p-3 min-h-[200px]">
              <div className="flex items-center justify-between px-2 py-2 mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: col.hex }}/>
                  <span className="text-xs font-semibold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{col.label}</span>
                </div>
                <span className="text-[10px] text-[#9CA3AF] bg-[#0B0D12] px-2 py-0.5 rounded-full border border-[#262A33]">{grouped[col.key]?.length || 0}</span>
              </div>
              <div className="space-y-2">
                {(grouped[col.key] || []).map(p => (
                  <button
                    key={p.project_id}
                    data-testid={`project-card-${p.project_id}`}
                    onClick={() => navigate(`/proyectos/${p.project_id}`)}
                    className="w-full text-left bg-[#0B0D12] border border-[#262A33] rounded-xl p-3 hover:border-[#F97316]/40 hover:bg-[#1A1D24] transition-colors"
                  >
                    <div className="text-sm text-[#F5F5F7] font-semibold mb-1 truncate" style={{ fontFamily: "Manrope, sans-serif" }}>{p.name}</div>
                    <div className="text-[11px] text-[#9CA3AF] truncate">{clientMap[p.client_id]?.name || "—"}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <ProgressBar value={p.progress} className="flex-1"/>
                      <span className="text-[10px] text-[#F97316] font-semibold">{p.progress}%</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-[#9CA3AF]">
                      <span className="flex items-center gap-1"><ListChecks className="w-3 h-3"/>{p.tasks_done}/{p.task_count}</span>
                      {p.due_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3"/>{p.due_date}</span>}
                    </div>
                    {teamMap[p.team_member_id] && (
                      <div className="mt-2 flex items-center gap-1.5 pt-2 border-t border-[#262A33]/50">
                        {teamMap[p.team_member_id].photo_url ? (
                          <img src={teamMap[p.team_member_id].photo_url} alt="" className="w-5 h-5 rounded-full object-cover"/>
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-[#262A33] flex items-center justify-center text-[9px] text-[#F5F5F7] font-bold">
                            {teamMap[p.team_member_id].name.charAt(0)}
                          </div>
                        )}
                        <span className="text-[10px] text-[#9CA3AF] truncate">{teamMap[p.team_member_id].name}</span>
                      </div>
                    )}
                  </button>
                ))}
                {(grouped[col.key]?.length || 0) === 0 && (
                  <div className="text-[11px] text-[#6B7280] text-center py-6">Sin proyectos</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#262A33] text-[#9CA3AF]">
                  <th className="text-left font-medium px-5 py-3">Proyecto</th>
                  <th className="text-left font-medium px-5 py-3">Cliente</th>
                  <th className="text-left font-medium px-5 py-3">Estado</th>
                  <th className="text-left font-medium px-5 py-3">Progreso</th>
                  <th className="text-left font-medium px-5 py-3">Responsable</th>
                  <th className="text-left font-medium px-5 py-3">Entrega</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr
                    key={p.project_id}
                    data-testid={`project-row-${p.project_id}`}
                    onClick={()=>navigate(`/proyectos/${p.project_id}`)}
                    className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-3">
                      <div className="text-[#F5F5F7] font-medium">{p.name}</div>
                      <div className="text-[11px] text-[#9CA3AF]">{p.tasks_done}/{p.task_count} tareas</div>
                    </td>
                    <td className="px-5 py-3 text-[#9CA3AF]">{clientMap[p.client_id]?.name || "—"}</td>
                    <td className="px-5 py-3"><StatusPill status={p.status}/></td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <ProgressBar value={p.progress} className="flex-1"/>
                        <span className="text-xs text-[#F97316] font-semibold w-9">{p.progress}%</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[#9CA3AF]">{teamMap[p.team_member_id]?.name || "—"}</td>
                    <td className="px-5 py-3 text-[#9CA3AF]">{p.due_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Nuevo proyecto</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Cliente *">
              <select data-testid="project-client-select" required className={inputCls} value={form.client_id} onChange={e=>setForm({...form, client_id: e.target.value})}>
                <option value="">Selecciona un cliente</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Nombre del proyecto *">
              <input data-testid="project-name-input" required className={inputCls} value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Campaña lanzamiento nuevo menú"/>
            </Field>
            {templates.length > 0 && (
              <Field label="Empezar desde plantilla" hint="Copiará las tareas de la plantilla al proyecto nuevo.">
                <select data-testid="project-template-select" className={inputCls} value={form.template_id} onChange={e=>setForm({...form, template_id: e.target.value})}>
                  <option value="">(sin plantilla)</option>
                  {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name} ({t.task_titles.length} tareas)</option>)}
                </select>
              </Field>
            )}
            <Field label="Descripción">
              <textarea rows={2} className={inputCls} value={form.description} onChange={e=>setForm({...form, description: e.target.value})}/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Estado">
                <select className={inputCls} value={form.status} onChange={e=>setForm({...form, status: e.target.value})}>
                  {STATUS_LIST.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Responsable">
                <select className={inputCls} value={form.team_member_id} onChange={e=>setForm({...form, team_member_id: e.target.value})}>
                  <option value="">(sin asignar)</option>
                  {team.map(t => <option key={t.member_id} value={t.member_id}>{t.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Inicio"><input type="date" className={inputCls} value={form.start_date} onChange={e=>setForm({...form, start_date: e.target.value})}/></Field>
              <Field label="Entrega estimada"><input type="date" className={inputCls} value={form.due_date} onChange={e=>setForm({...form, due_date: e.target.value})}/></Field>
            </div>
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-project-btn" type="submit">Crear proyecto</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Templates dialog */}
      <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Plantillas de proyecto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-[#9CA3AF] mb-2 font-medium">Mis plantillas ({templates.length})</div>
              {templates.length === 0 ? (
                <p className="text-sm text-[#6B7280] py-4 text-center">Aún no tienes plantillas. Crea una →</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-auto pr-1">
                  {templates.map(t => (
                    <div key={t.template_id} data-testid={`template-row-${t.template_id}`} className="p-3 rounded-xl bg-[#0B0D12] border border-[#262A33]">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[#F5F5F7] font-medium">{t.name}</div>
                          <div className="text-[11px] text-[#F97316] mt-0.5">{t.task_titles.length} tareas</div>
                        </div>
                        <button
                          data-testid={`delete-template-${t.template_id}`}
                          onClick={()=>removeTemplate(t)}
                          aria-label="Eliminar"
                          className="p-1 rounded text-[#6B7280] hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                      {t.task_titles.length > 0 && (
                        <ul className="mt-2 space-y-0.5">
                          {t.task_titles.slice(0, 5).map((tt, i) => (
                            <li key={i} className="text-[11px] text-[#9CA3AF]">· {tt}</li>
                          ))}
                          {t.task_titles.length > 5 && <li className="text-[10px] text-[#6B7280]">+{t.task_titles.length - 5} más</li>}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-[#9CA3AF] mb-2 font-medium">Nueva plantilla</div>
              <form onSubmit={saveTemplate} className="space-y-3">
                <Field label="Nombre">
                  <input data-testid="template-name-input" required className={inputCls} value={newTplForm.name} onChange={e=>setNewTplForm({...newTplForm, name: e.target.value})} placeholder="Ej: Campaña de aperturas"/>
                </Field>
                <Field label="Descripción">
                  <input className={inputCls} value={newTplForm.description} onChange={e=>setNewTplForm({...newTplForm, description: e.target.value})}/>
                </Field>
                <Field label="Tareas (una por línea)">
                  <textarea
                    data-testid="template-tasks-input"
                    rows={6}
                    className={inputCls}
                    value={newTplForm.tasksText}
                    onChange={e=>setNewTplForm({...newTplForm, tasksText: e.target.value})}
                    placeholder={"Diseñar carrusel Instagram\nGrabar reel del chef\nPublicar en Meta Ads"}
                  />
                </Field>
                <PrimaryButton data-testid="save-template-btn" type="submit" className="w-full justify-center">
                  <Plus className="w-4 h-4"/> Guardar plantilla
                </PrimaryButton>
              </form>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
